import React from 'react'

import { Box, Grid, Skeleton } from '@mui/material'

import { AnalyticsBlock } from 'components'
import type { AnalyticsMetricId } from 'components/analytics/analyticsSolutions'
import type { GetAnalyticsTotalsCounts } from 'store/analytics/types'

/**
 * The Overview KPI row.
 *
 * Uses the shared `AnalyticsBlock` — the canonical stat tile, and the same one the
 * Tags → Inventory tab renders — rather than the hand-rolled boxes that used to sit
 * here. Those diverged from the house surface on every axis (`grey[200]` instead of
 * the shared `Card`'s border, radius 2 instead of 3, `p:4` instead of 6, a `Divider`
 * nothing else has, `h1` instead of the 32px stat, and no `formatNumber`), which is
 * why the row read as a different design language from the rest of the platform.
 *
 * `term` is set per card so each stat's definition comes from the glossary rather
 * than a retyped tooltip (§44/§59) — a stat tile is exactly where a term of art
 * should be explainable in place.
 */
export interface OverviewStatBox {
  /**
   * ⚠️ AN INTERSECTION, and BOTH halves are load-bearing (VERA-260).
   *
   * `keyof GetAnalyticsTotalsCounts` is the original constraint and still earns its keep:
   * a typo in a key is a compile error rather than a tile silently rendering 0 via `?? 0`.
   *
   * `AnalyticsMetricId` is the second half, and it is what makes an uncategorised tile
   * impossible. It is the key of `ANALYTICS_METRIC_SOLUTIONS` — the table saying which
   * solution each metric is evidence for — so a fifth tile pointing at a real totals field
   * fails HERE until its key joins the union, and joining the union then fails in
   * `analyticsSolutions` until the metric is classified. `ROICardDef['id']` carries the
   * same type for the same reason; see its note for the full chain.
   *
   * The intersection is exactly the four keys below, since those are the only members of
   * both — which is the property that lets the narrowing be free today and binding later.
   */
  key: AnalyticsMetricId & keyof GetAnalyticsTotalsCounts
  title: string
  term?: string
}

/**
 * Exported because the canvas addresses these cards INDIVIDUALLY.
 *
 * A card in a saved view is identified by an id, and a KPI's id is the totals field it
 * displays — so this list is both the row's content and the registry the page maps into
 * `AnalyticsCardItem`s. `analyticsViewSeeds.ANALYTICS_TILE_IDS` names the same four
 * keys; they agree because both are `keyof GetAnalyticsTotalsCounts`, which is what
 * turns a typo into a compile error rather than a card that silently renders 0.
 */
/**
 * ── "Scans" / "Certificates", not "Total Scans" / "Total Certificates" (VERA-260) ───
 *
 * "Total" was noise on the KPI tile — the figure already sits under a period-scoped
 * "vs. previous N days" line (`AnalyticsBlock`), so the word said nothing the layout
 * doesn't already say. It became actively WRONG once these two became composable chart
 * SERIES names (VRG-467): a chart series titled "Total Scans" describes scans over
 * time, not a total, and a composed card's title (`sources.map(s => s.title).join(' +
 * ')`, in `analyticsDisplayVariants.tsx`) read "Total Scans + Total Certificates" —
 * doubly wrong there, since neither half of a two-series chart is a total either.
 *
 * ⚠️ These are DISPLAY NAMES ONLY. The underlying metric ids (`scans_count`,
 * `certificates_count`) are UNCHANGED and must stay that way — they are persisted in
 * every saved view's `cardIds` and every layout item's `i` (`analyticsGrid`'s variant-id
 * grammar), so renaming the id would orphan every existing saved view. Renaming `title`
 * costs nothing there: it is read fresh from this list on every render, never stored.
 */
/*
  ⚠️ THE TWO TRANSFER TILES POINT AT THEIR OWN COUNTS, NOT AT `transfer` (VERA-260).

  Both used to resolve to the noun — one of them to nothing at all — and the noun cannot
  answer the question either tile actually raises. Both are scoped by `transfers.created_at`
  (see `getTotalByRange`), so BOTH pick their rows by when the transfer was STARTED:

    Completed Transfers    started in the range, and now completed. NOT "completed in the
                           range" — one begun last month and accepted this month counts
                           in last month.
    In Progress Transfers  started in the range, and still open. The date is a range, the
                           STATUS is read now, so this figure falls over time without the
                           range changing.

  Read together they split one cohort of started transfers by where it got to, which is the
  only reading under which they are comparable — and none of that is derivable from a
  definition of the word "transfer". `transfers-completed` and `transfers-in-progress` say
  it; both `seeAlso` the noun, so nothing is cut off.
*/
export const OVERVIEW_STAT_BOXES: OverviewStatBox[] = [
  { key: 'scans_count', title: 'Scans', term: 'scan' },
  { key: 'certificates_count', title: 'Certificates', term: 'certificate' },
  { key: 'completed_transfers_count', title: 'Completed Transfers', term: 'transfers-completed' },
  { key: 'in_progress_transfers_count', title: 'In Progress Transfers', term: 'transfers-in-progress' },
]

interface OverviewProps {
  /**
   * Typed to the COUNTS, not `Record<string, number>`.
   *
   * The old signature was always a fiction — it claimed every property of the totals
   * response is a number — and it went unnoticed only because, until the prior-period
   * comparison landed, that happened to be true. Adding `previous` / `previous_range`
   * (objects) made the response stop satisfying it, and the resulting error pointed at
   * this prop rather than at the response, which is the wrong end.
   *
   * `keyof` on the key list is the part that earns its keep: a typo in a STAT_BOXES key
   * is now a compile error instead of a card silently rendering 0 via `?? 0`.
   */
  totals?: Partial<GetAnalyticsTotalsCounts>
  /**
   * The same counts over the immediately preceding window, when the request asked for
   * a comparison. Each card's delta is computed by `AnalyticsBlock` from this.
   *
   * Optional and passed through UNDEFINED rather than defaulted to `{}` or to zeros:
   * a missing comparison and a comparison that measured zero are different facts, and
   * `AnalyticsBlock` distinguishes them (no delta vs. "New"). Defaulting here would
   * collapse the two and make every card claim growth from nothing.
   */
  previous?: Partial<GetAnalyticsTotalsCounts>
  /**
   * A noun phrase naming the length of that window — "30 days", "week". Derived by the
   * caller from the range the server actually measured, so this component never has to
   * guess a period from the filters (which describe the CURRENT window, not the
   * comparison one).
   */
  interval?: string
  isLoading?: boolean
}

/**
 * Height reserved for every card in the row, skeleton included.
 *
 * The delta line is CONDITIONAL — `AnalyticsBlock` draws nothing for a metric that was
 * zero in BOTH windows — so without a floor the row goes ragged the moment one KPI is
 * quiet, and every card grows by the height of that line when the numbers arrive. Both
 * are the layout shift §19 exists to prevent, and the second is the one the skeleton is
 * supposed to absorb.
 *
 * Measured in the browser at 1280x800, not estimated: the card is **119.52px** without
 * the delta line and **159.52px** with it (a 28px row plus the outer Stack's 12px gap).
 * The floor is the taller of the two, so a card that renders no delta still occupies the
 * space one would, and the skeleton matches the same number.
 *
 * The skeleton was previously 132 — a value that matched neither state and jumped ~12px
 * on load even before this change.
 */
export const CARD_MIN_HEIGHT = 160

interface OverviewStatProps extends OverviewProps {
  box: OverviewStatBox
}

/**
 * ONE KPI card. The delta wiring lives here and nowhere else, so a card rendered
 * standalone in the canvas's tile strip is byte-identical to one rendered in the grid
 * below — including the `CARD_MIN_HEIGHT` floor, which is what stops the row going
 * ragged when a quiet metric renders no delta line.
 */
export const OverviewStat = ({
  box: { key, title, term },
  totals = {},
  previous,
  interval,
  isLoading = false,
}: OverviewStatProps) =>
  isLoading ? (
    // Footprint parity (§19): same height and radius as the loaded card, so
    // the row does not resize when the numbers arrive.
    <Skeleton variant="rounded" height={CARD_MIN_HEIGHT} sx={{ borderRadius: 3 }} />
  ) : (
    // The floor is applied from OUT here rather than passed to the card: this
    // component's `sx` prop is destructured but never spread onto its `Card`,
    // so an `sx` handed to it is silently dropped. Stretching the single child
    // keeps the card's own border and radius as the visible surface.
    //
    // `height: '100%'` and not `minHeight` alone. The floor was right when a tile sized
    // to its own content — it kept the row from going ragged (§19). Once the grid gave
    // every tile a DEFINITE 160px cell, a floor became a licence to GROW: measured, this
    // wrapper stretched to 183px around Geographic Reach's caveat and pushed the card
    // 23px past the cell, over the card below. The cell now owns the height and the
    // floor only applies where there is no cell.
    <Box sx={{ display: 'flex', height: '100%', minHeight: CARD_MIN_HEIGHT, maxHeight: '100%', '& > *': { flex: 1, minHeight: 0 } }}>
      <AnalyticsBlock
        title={title}
        term={term}
        stat={totals[key] ?? 0}
        // NOT `?? 0`. An absent comparison must stay absent — see `previous`.
        prevStat={previous?.[key]}
        interval={interval}
      />
    </Box>
  )

/**
 * The four KPIs as one grid row.
 *
 * Retained as a composition of `OverviewStat` rather than deleted: the Analytics page
 * now renders each stat as its own reorderable canvas tile, but the row is still the
 * correct rendering wherever the four are a fixed unit, and keeping one component for
 * the card means the two can never draw a different card.
 */
export const Overview = (props: OverviewProps) => (
  <Grid container spacing={3}>
    {OVERVIEW_STAT_BOXES.map(box => (
      <Grid item xs={12} sm={6} md={3} key={box.key}>
        <OverviewStat box={box} {...props} />
      </Grid>
    ))}
  </Grid>
)
